import { ChangeEventHandler, ClipboardEventHandler, useState } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";

import { styled, TextField, TextFieldProps } from "@mui/material";

import { useAppTranslation } from "@hooks";

import { CharCounter } from "./FormTextFieldCharCounter.styled";
import { isString } from "lodash";

interface FormTextFieldCharCounterProps<T extends FieldValues>
  extends Omit<TextFieldProps, "onChange" | "error" | "helperText"> {
  name: Path<T>;
  control?: Control<T>;
  maxLength: number;
  strict?: boolean;
}

export const FormTextFieldCharCounter = <T extends FieldValues>({
  name,
  control,
  maxLength,
  strict,
  ...props
}: FormTextFieldCharCounterProps<T>) => {
  const { t } = useAppTranslation();

  const [wasPasteTrimmed, setWasPasteTrimmed] = useState(false);

  const {
    field: { onChange, value, onBlur, ref },
    fieldState: { error },
  } = useController({ name, control });

  const handleChange: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = e => {
    if (wasPasteTrimmed) {
      setWasPasteTrimmed(false);
    }

    const inputValue = e.target.value;
    const isOverLimit = inputValue.length > maxLength;
    const isWriting = isString(value) ? inputValue.length > value.length : true;

    if (isOverLimit && isWriting && strict) return;

    onChange(inputValue);
  };

  const handlePaste: ClipboardEventHandler<HTMLDivElement> = e => {
    if (!strict) {
      return;
    }

    e.preventDefault();

    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    const pastedText = e.clipboardData.getData("text/plain");
    const valueBefore = target.value ?? "";
    const selectionStart = target.selectionStart ?? valueBefore.length;
    const selectionEnd = target.selectionEnd ?? selectionStart;

    const nextValue = (
      valueBefore.slice(0, selectionStart) +
      pastedText +
      valueBefore.slice(selectionEnd)
    ).slice(0, maxLength);

    const replacedLength = selectionEnd - selectionStart;
    const totalLength = valueBefore.length - replacedLength + pastedText.length;
    const wasTrimmed = totalLength > maxLength;

    setWasPasteTrimmed(wasTrimmed);
    onChange(nextValue);
  };

  const overLimit = isString(value) ? value.length >= maxLength : false;

  return (
    <div className="grid w-full">
      <TextField
        ref={ref}
        {...props}
        value={value}
        onChange={handleChange}
        onBlur={e => {
          onBlur();
          props.onBlur && props.onBlur(e);
        }}
        onPaste={handlePaste}
        error={!!error}
        helperText={
          error?.message ? (
            t(error.message)
          ) : overLimit ? (
            <HelperText>
              {t("common.max_char_limit", { count: maxLength })}
            </HelperText>
          ) : undefined
        }
      />
      <CharCounter error={overLimit}>
        {wasPasteTrimmed && t("common.paste_char_limit_exceeded")}
        {value?.length ?? 0} / {maxLength}
      </CharCounter>
    </div>
  );
};

const HelperText = styled("span")`
  color: ${({ theme }) => theme.palette.error.main};
`;
